import { useEffect, useState } from 'react';
import { MdClose } from 'react-icons/md';
import api from '../utils/api';
import { CsvUpload } from './IssueCertificates';
import CertificatePreview from './CertificatePreview';

interface Props {
    open: boolean;
    onClose: () => void;
    onIssued: () => void;
}

const empty = { firstName: '', lastName: '', organization: '', courseName: '', issueDate: '', duration: '', city: '' };
const steps = ['Details', 'Preview', 'Confirm'];

const IssueDrawer = ({ open, onClose, onIssued }: Props) => {
    const [mode, setMode] = useState<'single' | 'bulk'>('single');
    const [step, setStep] = useState(1);
    const [form, setForm] = useState(empty);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (open) {
            setStep(1);
            setError('');
        }
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    const set = (key: keyof typeof empty) => (e: React.ChangeEvent<HTMLInputElement>) =>
        setForm(prev => ({ ...prev, [key]: e.target.value }));

    const valid = !!(form.firstName.trim() && form.lastName.trim() && form.organization.trim() && form.courseName.trim() && form.issueDate);

    const issue = async () => {
        setLoading(true);
        setError('');
        try {
            await api.post('/v1/vitrine/certificates/issue', form);
            setForm(empty);
            onIssued();
            onClose();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to issue certificate');
        } finally {
            setLoading(false);
        }
    };

    const field = (key: keyof typeof empty, label: string, opts: { type?: string; placeholder?: string; required?: boolean } = {}) => (
        <div className="f-field">
            <label htmlFor={`issue-${key}`}>{label}</label>
            <input id={`issue-${key}`} type={opts.type || 'text'} value={form[key]} onChange={set(key)} placeholder={opts.placeholder} required={opts.required} />
        </div>
    );

    const name = `${form.firstName} ${form.lastName}`.trim();
    const previewData = { name, organization: form.organization, course: form.courseName, date: form.issueDate, duration: form.duration, city: form.city };

    return (
        <>
            <div className={`drawer-backdrop${open ? ' open' : ''}`} onClick={onClose} />
            <aside className={`issue-drawer${open ? ' open' : ''}`} aria-hidden={!open} aria-label="Issue certificate">
                <div className="drawer-head">
                    <h3>Issue certificate</h3>
                    <button className="icon-btn" onClick={onClose} aria-label="Close"><MdClose size={20} /></button>
                </div>

                <div className="drawer-tabs">
                    <div className="segmented">
                        <button className={mode === 'single' ? 'on' : ''} onClick={() => setMode('single')}>Single</button>
                        <button className={mode === 'bulk' ? 'on' : ''} onClick={() => setMode('bulk')}>Bulk CSV</button>
                    </div>
                </div>

                {mode === 'single' ? (
                    <>
                        <ol className="stepper">
                            {steps.map((s, i) => (
                                <li key={s} className={step === i + 1 ? 'on' : step > i + 1 ? 'done' : ''}>{i + 1} {s}</li>
                            ))}
                        </ol>

                        <div className="drawer-body">
                            {step === 1 && (
                                <>
                                    <div className="f-grid">
                                        {field('firstName', 'First name', { placeholder: 'Ahmed', required: true })}
                                        {field('lastName', 'Last name', { placeholder: 'Benali', required: true })}
                                    </div>
                                    {field('organization', 'Organization', { placeholder: 'Basma Center', required: true })}
                                    {field('courseName', 'Course name', { placeholder: 'Digital Skills Training', required: true })}
                                    <div className="f-grid three">
                                        {field('issueDate', 'Issue date', { type: 'date', required: true })}
                                        {field('duration', 'Hours', { placeholder: '24' })}
                                        {field('city', 'City', { placeholder: 'Tlemcen' })}
                                    </div>
                                </>
                            )}
                            {step === 2 && (
                                <>
                                    <CertificatePreview data={previewData} />
                                    <p className="drawer-hint">Check the spelling of the name. It is printed on the PDF and cannot be edited after issuing.</p>
                                </>
                            )}
                            {step === 3 && (
                                <div className="confirm-box">
                                    <dl>
                                        <dt>Recipient</dt><dd>{name}</dd>
                                        <dt>Organization</dt><dd>{form.organization}</dd>
                                        <dt>Course</dt><dd>{form.courseName}</dd>
                                        <dt>Issue date</dt><dd>{form.issueDate}</dd>
                                    </dl>
                                    <p className="drawer-hint">A hash is generated and the PDF is created when you confirm.</p>
                                    {error && <div className="inline-alert" role="alert">{error}</div>}
                                </div>
                            )}
                        </div>

                        <div className="drawer-foot">
                            <button className="btn-outline" onClick={() => (step === 1 ? onClose() : setStep(step - 1))} disabled={loading}>
                                {step === 1 ? 'Cancel' : 'Back'}
                            </button>
                            <button
                                className="btn-solid"
                                disabled={loading || (step === 1 && !valid)}
                                onClick={() => (step === 3 ? issue() : setStep(step + 1))}
                            >
                                {step === 3 ? (loading ? 'Issuing...' : 'Issue certificate') : 'Continue'}
                            </button>
                        </div>
                    </>
                ) : (
                    <div className="drawer-body">
                        <CsvUpload onIssued={onIssued} />
                    </div>
                )}
            </aside>
        </>
    );
};

export default IssueDrawer;
